import React, { useEffect, useState } from "react";
import ResultsFilter from "../components/results/ResultsFilter";
import StatsOverview from "../components/results/StatsOverview";
import MatchHistoryList from "../components/results/MatchHistoryList";
import MatchCard from "../components/results/MatchCard";
import { fetchAllResults } from "../store/slice/result";
import { fetchGameMeta } from "../store/slice/gamesMeta";
import { useDispatch, useSelector } from "react-redux";
import MatchResultSummaryPopUp from "../components/results/MatchResultSummaryPopUp";
import MatchStatsModal from "../components/results/MatchStatsModal";
import CustomLoader from "../components/widgets/custom_loader";
import { mapStatus } from "../utils/matchStatus";
import GameService from "../services/game.service";
import { toastError } from "../common/toastNotification";

const groupByDate = (games = []) => {
  const map = {};

  games.forEach((game) => {
    const date = new Date(game.createdAt).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    if (!map[date]) {
      map[date] = [];
    }

    map[date].push(game);
  });

  return Object.entries(map)
    .sort((a, b) => new Date(b[0]) - new Date(a[0]))
    .map(([date, matches]) => ({
      date,
      matches,
    }));
};

const normalizeGame = (game, gameMetaData) => {
  const { status, result } = mapStatus(game);
  const displayName = gameMetaData?.lobbyMeta?.[game.gameName]?.displayName;

  return {
    id: game._id, // for React key
    registrationId: game.gameRegistrationId, // 🔑 ADD THIS
    canReveal: game.canReveal === true,

    game: displayName || game.gameName
      ?.split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" "),

    opponent: game.opponent?.username,
    status,
    result,

    entryFee: game.entryFee
      ? game.entryFee.value.toString()
      : "0",

    amount:
      status === "win" && game.amountWon?.value > 0
        ? game.amountWon.value.toString()
        : "0",
    currencyCode: game.currencyCode,
    createdAt: game.createdAt,
  };
};
const Results = ({ gameName }) => {
  const dispatch = useDispatch();
  const [matchResult, setMatchResult] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [selectedGame, setSelectedGame] = useState(gameName || "all-games");
  const [selectedFormat, setSelectedFormat] = useState("all-formats");
  const { data: apiResults, status: resultStatus } = useSelector((state) => state.result);
  const { status: metaStatus, data: gameMetaData } = useSelector((state) => state.gamesMeta);

  useEffect(() => {
    const args = {};
    if (selectedGame !== "all-games") args.gameName = selectedGame;
    if (selectedFormat !== "all-formats") args.lobbyFormat = selectedFormat;
    dispatch(fetchAllResults(args));
  }, [dispatch, selectedGame, selectedFormat]);

  useEffect(() => {
    if (metaStatus === 'idle') {
      dispatch(fetchGameMeta());
    }
  }, [metaStatus, dispatch]);

  const games = apiResults?.data?.games || [];
  const isLoadingResults = resultStatus === 'loading';

  const handleFilterChange = (filterType, value) => {
    if (filterType === "game") setSelectedGame(value);
    if (filterType === "format") setSelectedFormat(value);
  };

  const userStats = apiResults?.data?.userStats;

  const pendingGames = games
    .filter((g) => g.gameResult === "MATCH_IN_PROGRESS")
    .map((g) => normalizeGame(g, gameMetaData));

  const completedGames = games
    .filter((g) => g.gameResult !== "MATCH_IN_PROGRESS")
    .map((g) => normalizeGame(g, gameMetaData));
  const history = groupByDate(completedGames);

  const handleReveal = async (registrationId) => {
    const match = games.find((g) => g.gameRegistrationId === registrationId);
    if (!match) return;
    const { status, result } = mapStatus(match);
    setShowStats(false);

    setMatchResult({
      matchId: match._id,
      amount: Number(match.amountWon?.value || 0),
      currencyCode: match.currencyCode,
      userName: match.player?.username || "You",
      userScore: match.score ?? null,
      opponentName: match.opponent?.username || null,
      opponentScore: match.opponentScore ?? null,
      gameName: match.gameName,
      status,
      result,
      isPending: match.gameResult === "MATCH_IN_PROGRESS",
    });

    if (match.canReveal) {
      try {
        await GameService.revealMatchResult(registrationId);
        const args = {};
        if (selectedGame !== "all-games") args.gameName = selectedGame;
        if (selectedFormat !== "all-formats") args.lobbyFormat = selectedFormat;
        dispatch(fetchAllResults(args));
      } catch (e) {
        toastError("Couldn't mark result as revealed. Please try again.");
      }
    }

    // The history list omits `breakdown` (it would bloat the payload). Fetch the
    // match detail to enrich the shown result with per-round data for Match
    // Stats. Best-effort: in-progress matches have none and the endpoint 409s.
    try {
      const json = await GameService.getMatchResultByRegistrationId(registrationId);
      const detail = json?.data?.match;
      if (detail?.breakdown?.length) {
        setMatchResult((prev) =>
          prev
            ? {
                ...prev,
                breakdown: detail.breakdown,
                opponentBreakdown: detail.opponentBreakdown,
              }
            : prev,
        );
      }
    } catch (e) {
      // No per-round stats available — Match Stats button stays hidden.
    }
  };

  // Filters are hidden for all clients for now; flip to re-enable.
  const showFilters = false;

  return (
    <div className="pt-6 pb-24">

      {showFilters && (
        <ResultsFilter onFilterChange={handleFilterChange} defaultGame={selectedGame} />
      )}
      <StatsOverview stats={userStats} />

      {isLoadingResults ? (
        <div className="py-12">
          <CustomLoader />
        </div>
      ) : (
        <>
          {/* Pending Games Section */}
          {pendingGames.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm text-muted-foreground mb-3 px-1">Pending Results</h3>
              <div className="space-y-2">
                {pendingGames.map((match) => (
                  <MatchCard
                    key={match.id}
                    match={match}
                    onReveal={handleReveal}
                  />
                ))}
              </div>
            </div>
          )}

          <MatchHistoryList history={history} onReveal={handleReveal} />
        </>
      )}

      {matchResult && (
        <MatchResultSummaryPopUp
          amount={matchResult.amount}
          userName={matchResult.userName}
          userScore={matchResult.userScore}
          opponentName={matchResult.opponentName}
          opponentScore={matchResult.opponentScore}
          status={matchResult.status}
          result={matchResult.result}
          isPending={matchResult.isPending}
          breakdown={matchResult.breakdown}
          onMatchStats={() => setShowStats(true)}
          onClose={() => {
            setMatchResult(null);
            setShowStats(false);
          }}
          currencyCode={matchResult.currencyCode}
          matchId={matchResult.matchId}
        />
      )}
      {matchResult && showStats && (
        <MatchStatsModal
          gameName={matchResult.gameName}
          breakdown={matchResult.breakdown}
          opponentBreakdown={matchResult.opponentBreakdown}
          userName={matchResult.userName}
          opponentName={matchResult.opponentName}
          userScore={matchResult.userScore}
          opponentScore={matchResult.opponentScore}
          status={matchResult.status}
          onBack={() => setShowStats(false)}
        />
      )}
    </div>
  );
};

export default Results;

